import React, { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { 
  Building2, 
  Plus, 
  Edit, 
  Trash2, 
  Search,
  X,
  Save,
  AlertCircle,
  CheckCircle
} from 'lucide-react'
import { toast } from 'react-hot-toast'
import { propertiesService } from '../services/api'

const Propiedades = () => {
  const queryClient = useQueryClient()
  
  // Estados
  const [searchTerm, setSearchTerm] = useState('')
  const [showActiveOnly, setShowActiveOnly] = useState(true)
  const [currentPage, setCurrentPage] = useState(1)
  const [showModal, setShowModal] = useState(false)
  const [editingProperty, setEditingProperty] = useState(null)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [propertyToDelete, setPropertyToDelete] = useState(null)
  
  // Formulario
  const [formData, setFormData] = useState({
    number: '',
    tower: '',
    floor: '',
    area: '',
    coefficient: ''
  })
  const [formErrors, setFormErrors] = useState({})

  // Query para obtener propiedades
  const { data: propertiesData, isLoading, error } = useQuery(
    ['properties', currentPage, searchTerm, showActiveOnly],
    () => propertiesService.getAll(currentPage, 10, searchTerm, '', showActiveOnly),
    {
      keepPreviousData: true,
      onError: (error) => {
        console.error('Error cargando propiedades:', error)
        toast.error('Error al cargar las propiedades')
      }
    }
  )

  // Mutación para crear propiedad
  const createMutation = useMutation(
    (data) => propertiesService.create(data),
    {
      onSuccess: () => {
        toast.success('Propiedad creada exitosamente')
        queryClient.invalidateQueries('properties')
        closeModal()
      },
      onError: (error) => {
        console.error('Error creando propiedad:', error)
        const errorMessage = error.response?.data?.detail || 'Error al crear la propiedad'
        toast.error(errorMessage)
      }
    }
  )

  // Mutación para actualizar propiedad
  const updateMutation = useMutation(
    ({ id, data }) => propertiesService.update(id, data),
    {
      onSuccess: () => {
        toast.success('Propiedad actualizada exitosamente')
        queryClient.invalidateQueries('properties')
        closeModal()
      },
      onError: (error) => {
        console.error('Error actualizando propiedad:', error)
        const errorMessage = error.response?.data?.detail || 'Error al actualizar la propiedad'
        toast.error(errorMessage)
      }
    }
  )

  // Mutación para eliminar propiedad
  const deleteMutation = useMutation(
    (id) => propertiesService.delete(id),
    {
      onSuccess: () => {
        toast.success('Propiedad eliminada exitosamente')
        queryClient.invalidateQueries('properties')
        setShowDeleteConfirm(false)
        setPropertyToDelete(null)
      },
      onError: (error) => {
        console.error('Error eliminando propiedad:', error)
        const errorMessage = error.response?.data?.detail || 'Error al eliminar la propiedad'
        toast.error(errorMessage)
      }
    }
  )

  // Validar formulario
  const validateForm = () => {
    const errors = {}

    if (!formData.number || formData.number.trim() === '') {
      errors.number = 'El número de apartamento es obligatorio'
    }

    if (!formData.coefficient || formData.coefficient === '') {
      errors.coefficient = 'El coeficiente es obligatorio'
    } else {
      const coef = parseFloat(formData.coefficient)
      if (isNaN(coef) || coef <= 0) {
        errors.coefficient = 'El coeficiente debe ser un número mayor a 0'
      }
    }

    if (formData.floor && formData.floor !== '') {
      const floor = parseInt(formData.floor)
      if (isNaN(floor) || floor < 0) {
        errors.floor = 'El piso debe ser un número válido mayor o igual a 0'
      }
    }

    if (formData.area && formData.area !== '') {
      const area = parseFloat(formData.area)
      if (isNaN(area) || area <= 0) {
        errors.area = 'El área debe ser un número mayor a 0'
      }
    }

    setFormErrors(errors)
    return Object.keys(errors).length === 0
  }

  // Manejar envío del formulario
  const handleSubmit = (e) => {
    e.preventDefault()
    
    if (!validateForm()) {
      toast.error('Por favor corrige los errores en el formulario')
      return
    }

    // Preparar datos para enviar
    const dataToSend = {
      number: formData.number.trim(),
      tower: formData.tower.trim() || null,
      floor: formData.floor ? parseInt(formData.floor) : null,
      area: formData.area ? parseFloat(formData.area) : null,
      coefficient: parseFloat(formData.coefficient)
    }

    if (editingProperty) {
      updateMutation.mutate({ id: editingProperty.id, data: dataToSend })
    } else {
      createMutation.mutate(dataToSend)
    }
  }

  // Abrir modal para crear
  const openCreateModal = () => {
    setEditingProperty(null)
    setFormData({
      number: '',
      tower: '',
      floor: '',
      area: '',
      coefficient: '1.0'
    })
    setFormErrors({})
    setShowModal(true)
  }

  // Abrir modal para editar
  const openEditModal = (property) => {
    setEditingProperty(property)
    setFormData({
      number: property.number || '',
      tower: property.tower || '',
      floor: property.floor !== null && property.floor !== undefined ? property.floor.toString() : '',
      area: property.area !== null && property.area !== undefined ? property.area.toString() : '',
      coefficient: property.coefficient !== null && property.coefficient !== undefined ? property.coefficient.toString() : '1.0'
    })
    setFormErrors({})
    setShowModal(true)
  }

  // Cerrar modal
  const closeModal = () => {
    setShowModal(false)
    setEditingProperty(null)
    setFormData({
      number: '',
      tower: '',
      floor: '',
      area: '',
      coefficient: ''
    })
    setFormErrors({})
  }

  // Confirmar eliminación
  const confirmDelete = (property) => {
    setPropertyToDelete(property)
    setShowDeleteConfirm(true)
  }

  // Ejecutar eliminación
  const handleDelete = () => {
    if (propertyToDelete) {
      deleteMutation.mutate(propertyToDelete.id)
    }
  }

  // Manejar cambios en el formulario
  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    // Limpiar error del campo
    if (formErrors[field]) {
      setFormErrors(prev => ({ ...prev, [field]: null }))
    }
  }

  const properties = propertiesData?.data?.items || []
  const totalPages = propertiesData?.data?.pages || 1

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Propiedades</h1>
          <p className="mt-2 text-gray-600">
            Gestión de propiedades del conjunto
          </p>
        </div>
        <button
          onClick={openCreateModal}
          className="btn btn-primary"
        >
          <Plus className="h-4 w-4 mr-2" />
          Nueva Propiedad
        </button>
      </div>

      {/* Filtros y búsqueda */}
      <div className="card">
        <div className="card-content">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Buscar por número de apartamento o torre..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="input pl-10"
                />
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={showActiveOnly}
                  onChange={(e) => setShowActiveOnly(e.target.checked)}
                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <span className="text-sm text-gray-700">Solo activas</span>
              </label>
            </div>
          </div>
        </div>
      </div>

      {/* Tabla de propiedades */}
      <div className="card">
        <div className="card-content p-0">
          {isLoading ? (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center py-12 text-red-600">
              <AlertCircle className="h-12 w-12 mb-4" />
              <p>Error al cargar las propiedades</p>
            </div>
          ) : properties.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-gray-500">
              <Building2 className="h-12 w-12 mb-4" />
              <p>No se encontraron propiedades</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Número
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Torre
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Piso
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Área (m²)
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Coeficiente
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Estado
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Acciones
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {properties.map((property) => (
                    <tr key={property.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {property.number}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {property.tower || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {property.floor !== null && property.floor !== undefined ? property.floor : '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {property.area ? parseFloat(property.area).toFixed(2) : '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {property.coefficient ? parseFloat(property.coefficient).toFixed(6) : '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          property.is_active 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {property.is_active ? 'Activa' : 'Inactiva'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <button
                          onClick={() => openEditModal(property)}
                          className="text-primary-600 hover:text-primary-900 mr-4"
                          title="Editar"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => confirmDelete(property)}
                          className="text-red-600 hover:text-red-900"
                          title="Eliminar"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Paginación */}
        {totalPages > 1 && (
          <div className="card-footer">
            <div className="flex items-center justify-between">
              <button
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                className="btn btn-secondary btn-sm"
              >
                Anterior
              </button>
              <span className="text-sm text-gray-700">
                Página {currentPage} de {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
                className="btn btn-secondary btn-sm"
              >
                Siguiente
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Modal de crear/editar */}
      {showModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-screen items-center justify-center p-4">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={closeModal}></div>
            
            <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">
                  {editingProperty ? 'Editar Propiedad' : 'Nueva Propiedad'}
                </h3>
              </div>

              <form onSubmit={handleSubmit} className="px-6 py-4 space-y-4">
                {/* Número de apartamento */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Número de Apartamento *
                  </label>
                  <input
                    type="text"
                    value={formData.number}
                    onChange={(e) => handleInputChange('number', e.target.value)}
                    className={`input ${formErrors.number ? 'border-red-300' : ''}`}
                    placeholder="Ej: 101, 201A, etc."
                  />
                  {formErrors.number && (
                    <p className="mt-1 text-sm text-red-600">{formErrors.number}</p>
                  )}
                </div>

                {/* Torre */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Torre
                  </label>
                  <input
                    type="text"
                    value={formData.tower}
                    onChange={(e) => handleInputChange('tower', e.target.value)}
                    className="input"
                    placeholder="Ej: A, B, Torre 1, etc."
                  />
                </div>

                {/* Piso */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Piso
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={formData.floor}
                    onChange={(e) => handleInputChange('floor', e.target.value)}
                    className={`input ${formErrors.floor ? 'border-red-300' : ''}`}
                    placeholder="Ej: 1, 2, 3, etc."
                  />
                  {formErrors.floor && (
                    <p className="mt-1 text-sm text-red-600">{formErrors.floor}</p>
                  )}
                </div>

                {/* Área */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Área (m²)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.area}
                    onChange={(e) => handleInputChange('area', e.target.value)}
                    className={`input ${formErrors.area ? 'border-red-300' : ''}`}
                    placeholder="Ej: 85.5"
                  />
                  {formErrors.area && (
                    <p className="mt-1 text-sm text-red-600">{formErrors.area}</p>
                  )}
                </div>

                {/* Coeficiente */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Coeficiente de Participación *
                  </label>
                  <input
                    type="number"
                    step="0.000001"
                    min="0"
                    value={formData.coefficient}
                    onChange={(e) => handleInputChange('coefficient', e.target.value)}
                    className={`input ${formErrors.coefficient ? 'border-red-300' : ''}`}
                    placeholder="Ej: 0.012345"
                  />
                  {formErrors.coefficient && (
                    <p className="mt-1 text-sm text-red-600">{formErrors.coefficient}</p>
                  )}
                  <p className="mt-1 text-xs text-gray-500">
                    Valor decimal que representa la participación en gastos comunes
                  </p>
                </div>

                {/* Botones */}
                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={closeModal}
                    className="btn btn-secondary"
                  >
                    <X className="h-4 w-4 mr-2" />
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    disabled={createMutation.isLoading || updateMutation.isLoading}
                    className="btn btn-primary"
                  >
                    {(createMutation.isLoading || updateMutation.isLoading) ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Guardando...
                      </>
                    ) : (
                      <>
                        <Save className="h-4 w-4 mr-2" />
                        {editingProperty ? 'Actualizar' : 'Crear'}
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Modal de confirmación de eliminación */}
      {showDeleteConfirm && propertyToDelete && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-screen items-center justify-center p-4">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setShowDeleteConfirm(false)}></div>
            
            <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full">
              <div className="px-6 py-4">
                <div className="flex items-center mb-4">
                  <AlertCircle className="h-6 w-6 text-red-600 mr-3" />
                  <h3 className="text-lg font-medium text-gray-900">
                    Confirmar Eliminación
                  </h3>
                </div>
                <p className="text-sm text-gray-500 mb-4">
                  ¿Estás seguro de que deseas eliminar la propiedad <strong>{propertyToDelete.number}</strong>
                  {propertyToDelete.tower && ` (Torre ${propertyToDelete.tower})`}?
                  Esta acción desactivará la propiedad.
                </p>
                <div className="flex justify-end space-x-3">
                  <button
                    onClick={() => setShowDeleteConfirm(false)}
                    className="btn btn-secondary"
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={handleDelete}
                    disabled={deleteMutation.isLoading}
                    className="btn bg-red-600 hover:bg-red-700 text-white"
                  >
                    {deleteMutation.isLoading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Eliminando...
                      </>
                    ) : (
                      <>
                        <Trash2 className="h-4 w-4 mr-2" />
                        Eliminar
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default Propiedades
